import {
  FormControl,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  useColorModeValue as mode,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { OrganizationLimitation } from '../../services/organization/organization.type';

// Custom Toggle Switch Component
const CustomToggle = ({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) => {
  const bgColor = mode('#10b981', '#10b981'); // Green for ON state
  const offBgColor = mode('#f3f4f6', '#4a5568'); // Light gray for OFF state
  const textColor = mode('#6b7280', '#e2e8f0'); // Dark gray for OFF text
  const whiteText = mode('white', 'white'); // White for ON text

  return (
    <div
      onClick={onChange}
      style={{
        position: 'relative',
        width: '80px',
        height: '24px',
        backgroundColor: checked ? bgColor : offBgColor,
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '11px',
        fontWeight: '600',
        color: checked ? whiteText : textColor,
        userSelect: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: checked ? 'calc(100% - 20px)' : '2px',
          width: '18px',
          height: '18px',
          backgroundColor: 'white',
          borderRadius: '50%',
          transition: 'all 0.3s ease',
          boxShadow: '0px 0px 1px 3px rgba(0, 0, 0, 0.2)',
        }}
      />
      <span
        style={{
          zIndex: 1,
          position: 'relative',
          marginLeft: checked ? '0px' : '20px',
          marginRight: checked ? '20px' : '0px',
          transition: 'all 0.3s ease',
        }}
      >
        {label}
      </span>
    </div>
  );
};

interface LimitationsFormProps {
  limitations: Partial<OrganizationLimitation>;
  unlimitedFlags: {
    question: boolean;
    storage: boolean;
    translation: boolean;
    expiration: boolean;
  };
  onToggleUnlimited: (field: 'question' | 'storage' | 'translation' | 'expiration') => void;
  onInputChange: (field: string, value: string | number) => void;
  isEdit?: boolean; // To differentiate between create and edit modes
}

const LimitationsForm: React.FC<LimitationsFormProps> = ({
  limitations,
  unlimitedFlags,
  onToggleUnlimited,
  onInputChange,
  isEdit = false,
}) => {
  const { t } = useTranslation();

  return (
    <VStack w='100%' spacing={4}>
      {/* Limitations - 2 columns layout */}
      <HStack w='100%' spacing={4}>
        <FormControl>
          <HStack align='center' justify='space-between' mb={2}>
            <FormLabel mb={0} color='text.default' fontSize='sm' fontWeight={500}>
              {t('managementSystem.editCompany.questionLimit')}
            </FormLabel>
            <CustomToggle
              checked={unlimitedFlags.question}
              onChange={() => onToggleUnlimited('question')}
              label={
                unlimitedFlags.question
                  ? t('managementSystem.toggle.unlimited')
                  : t('managementSystem.toggle.limited')
              }
            />
          </HStack>
          {unlimitedFlags.question && <div style={{ height: '40px' }}></div>}
          {!unlimitedFlags.question && (
            <InputGroup>
              <InputLeftAddon
                color='text.muted'
                bg='bg.surface'
                border='1px solid'
                borderColor='border.default'
                borderRightRadius={0}
              >
                {t('common.monthly')}
              </InputLeftAddon>
              <Input
                color='text.default'
                bg='bg.canvas'
                border='1px solid'
                borderColor='border.default'
                borderLeftRadius={0}
                _hover={{ borderColor: 'border.strong' }}
                _focus={{
                  borderColor: 'primary.solid',
                  boxShadow: '0 0 0 1px var(--chakra-colors-primary-solid)',
                }}
                _placeholder={{ color: 'text.subtle' }}
                min='1'
                onBlur={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    // Revert to original value if empty on blur
                    onInputChange('limitation.question', limitations.question || 1000);
                  }
                }}
                onChange={(e) => onInputChange('limitation.question', parseInt(e.target.value))}
                onKeyDown={(e) => {
                  if (e.key === 'e' || e.key === 'E') {
                    e.preventDefault();
                  }
                }}
                type='number'
                value={limitations.question ?? ''}
              />
            </InputGroup>
          )}
        </FormControl>

        <FormControl>
          <HStack align='center' justify='space-between' mb={2}>
            <FormLabel mb={0} color='text.default' fontSize='sm' fontWeight={500}>
              {t('managementSystem.editCompany.storageLimit')}
            </FormLabel>
            <CustomToggle
              checked={unlimitedFlags.storage}
              onChange={() => onToggleUnlimited('storage')}
              label={
                unlimitedFlags.storage
                  ? t('managementSystem.toggle.unlimited')
                  : t('managementSystem.toggle.limited')
              }
            />
          </HStack>
          {unlimitedFlags.storage && <div style={{ height: '40px' }}></div>}
          {!unlimitedFlags.storage && (
            <InputGroup>
              <Input
                color='text.default'
                bg='bg.canvas'
                border='1px solid'
                borderColor='border.default'
                borderRightRadius={0}
                _hover={{ borderColor: 'border.strong' }}
                _focus={{
                  borderColor: 'primary.solid',
                  boxShadow: '0 0 0 1px var(--chakra-colors-primary-solid)',
                }}
                _placeholder={{ color: 'text.subtle' }}
                onBlur={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    // Revert to original value if empty on blur
                    onInputChange('limitation.storage_gb', limitations.storage_gb || 10);
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow any input, validation will be done on submit
                  if (value === '') {
                    onInputChange('limitation.storage_gb', '');
                  } else {
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue)) {
                      onInputChange('limitation.storage_gb', numValue);
                    }
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'e' || e.key === 'E') {
                    e.preventDefault();
                  }
                }}
                type='number'
                value={
                  limitations.storage_gb === null || limitations.storage_gb === undefined
                    ? ''
                    : limitations.storage_gb
                }
              />
              <InputRightAddon
                color='text.muted'
                bg='bg.surface'
                border='1px solid'
                borderColor='border.default'
                borderLeft='none'
                borderLeftRadius={0}
              >
                GB
              </InputRightAddon>
            </InputGroup>
          )}
        </FormControl>
      </HStack>

      <HStack w='100%' spacing={4}>
        <FormControl>
          <HStack align='center' justify='space-between' mb={2}>
            <FormLabel mb={0} color='text.default' fontSize='sm' fontWeight={500}>
              {t('managementSystem.editCompany.translationLimit')}
            </FormLabel>
            <CustomToggle
              checked={unlimitedFlags.translation}
              onChange={() => onToggleUnlimited('translation')}
              label={
                unlimitedFlags.translation
                  ? t('managementSystem.toggle.unlimited')
                  : t('managementSystem.toggle.limited')
              }
            />
          </HStack>
          {unlimitedFlags.translation && <div style={{ height: '40px' }}></div>}
          {!unlimitedFlags.translation && (
            <InputGroup>
              <InputLeftAddon
                color='text.muted'
                bg='bg.surface'
                border='1px solid'
                borderColor='border.default'
                borderRightRadius={0}
              >
                {t('common.monthly')}
              </InputLeftAddon>
              <Input
                color='text.default'
                bg='bg.canvas'
                border='1px solid'
                borderColor='border.default'
                borderLeftRadius={0}
                _hover={{ borderColor: 'border.strong' }}
                _focus={{
                  borderColor: 'primary.solid',
                  boxShadow: '0 0 0 1px var(--chakra-colors-primary-solid)',
                }}
                _placeholder={{ color: 'text.subtle' }}
                min='1'
                onBlur={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    // Revert to original value if empty on blur
                    onInputChange('limitation.translation', limitations.translation || 10);
                  }
                }}
                onChange={(e) => onInputChange('limitation.translation', parseInt(e.target.value))}
                onKeyDown={(e) => {
                  if (e.key === 'e' || e.key === 'E') {
                    e.preventDefault();
                  }
                }}
                type='number'
                value={limitations.translation ?? ''}
              />
            </InputGroup>
          )}
        </FormControl>

        <FormControl>
          <HStack align='center' justify='space-between' mb={2}>
            <FormLabel mb={0} color='text.default' fontSize='sm' fontWeight={500}>
              {t('managementSystem.editCompany.expirationDate')}
            </FormLabel>
            <CustomToggle
              checked={unlimitedFlags.expiration}
              onChange={() => onToggleUnlimited('expiration')}
              label={
                unlimitedFlags.expiration
                  ? t('managementSystem.toggle.unlimited')
                  : t('managementSystem.toggle.limited')
              }
            />
          </HStack>
          {unlimitedFlags.expiration && <div style={{ height: '40px' }}></div>}
          {!unlimitedFlags.expiration && (
            <Input
              color='text.default'
              bg='bg.canvas'
              border='1px solid'
              borderColor='border.default'
              _hover={{ borderColor: 'border.strong' }}
              _focus={{
                borderColor: 'primary.solid',
                boxShadow: '0 0 0 1px var(--chakra-colors-primary-solid)',
              }}
              _placeholder={{ color: 'text.subtle' }}
              min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
              onChange={(e) => onInputChange('limitation.expiration', e.target.value)}
              placeholder='dd/mm/yyyy'
              type='date'
              value={limitations.expiration ?? ''}
            />
          )}
        </FormControl>
      </HStack>
    </VStack>
  );
};

export default LimitationsForm;
